import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Box } from '@mui/material';
import { SUBSCRIPTION_PLAN as SUBSCRIPTION_PLAN_PATH } from 'constants/paths';
import { useSubscriptionsInfo } from 'constants/subscriptions';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getIsSubscriptionLoading, getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { useToggle } from 'hooks/useToggler';

import { SUBSCRIPTION_STATUS } from 'interfaces/subscriptions';

import { Loader } from 'components/_basic/Loader/Loader';
import { SectionWrapper } from 'components/_basic/SectionWrapper/SectionWrapper';
import { BUTTON_VARIANTS } from 'components/_basic/SectionWrapper/types';
import { CancelSubscriptionModal } from 'components/CancelSubscriptionModal/CancelSubscriptionModal';
import { SubscriptionHorizontalCard } from 'components/SubscriptionHorizontalCard/SubscriptionHorizontalCard';

import { useMapUserSubscriptions } from 'helpers/mapUserSubscriptions';

import styles from './UserSubscriptionDetails.module.scss';

export const UserSubscriptionDetails: React.FC = () => {
  const navigate = useNavigate();
  const subscriptions = useSubscriptionsInfo();

  const [isCancelModalOpened, setIsCancelModalOpened] = useToggle(false);

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isSubscriptionLoading = useUserSubscriptionStore(getIsSubscriptionLoading);

  const subscriptionsToDisplay = useMapUserSubscriptions(userSubscription);

  if (isSubscriptionLoading || isEmpty(userSubscription)) {
    return <Loader />;
  }

  return (
    <>
      {subscriptionsToDisplay.map((subscriptionItem) => (
        <Box marginBottom={2} key={subscriptionItem.title}>
          <SubscriptionHorizontalCard {...subscriptionItem} />
        </Box>
      ))}
      {subscriptionsToDisplay.length < subscriptions.length &&
        userSubscription.currentSubscription.status !== SUBSCRIPTION_STATUS.CANCELLED_MANUALLY && (
          <>
            <SectionWrapper
              sectionTitle={t('downgradeSubscription')}
              buttonText={t('viewPlans')}
              buttonClickHandler={(): void => navigate(SUBSCRIPTION_PLAN_PATH)}
            />
            <SectionWrapper
              sectionTitle={t('cancelSubscription')}
              buttonText={t('cancel')}
              buttonClickHandler={setIsCancelModalOpened}
              buttonVariant={BUTTON_VARIANTS.RED}
              className={styles.subscriptionAction}
            />
            <CancelSubscriptionModal
              isOpened={isCancelModalOpened}
              handleClose={setIsCancelModalOpened}
              userSubscription={userSubscription}
            />
          </>
        )}
    </>
  );
};
